import React, { useState, useEffect, useCallback } from 'react';
import { getSubmissions, getInterviewDefinitions, reactivateSubmission } from '../api/api';
import './Submissions.css';

const Submissions = () => {
    const [submissions, setSubmissions] = useState([]);
    const [definitions, setDefinitions] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterUser, setFilterUser] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [subs, defs] = await Promise.all([
                getSubmissions(filterStatus || undefined, filterUser || undefined),
                getInterviewDefinitions(),
            ]);
            setSubmissions(subs);
            // Create a lookup map for definitions
            const defMap = {};
            defs.forEach(d => { defMap[d._id] = d; });
            setDefinitions(defMap);
        } catch (err) {
            setError(err.message || 'Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterUser]);

    useEffect(() => { loadData(); }, [loadData]);

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handleReactivate = async (id) => {
        if (!window.confirm('¿Seguro que quieres reactivar esta encuesta rechazada?')) return;
        try {
            await reactivateSubmission(id);
            alert('Encuesta reactivada');
            loadData();
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    const getStatusBadge = (status) => {
        const labels = {
            'in_progress': 'En progreso',
            'completed': 'Completada',
            'pending': 'Pendiente',
            'refused': 'Rechazada',
        };
        const classes = {
            'in_progress': 'badge badge-progress',
            'completed': 'badge badge-done',
            'pending': 'badge badge-pending',
            'refused': 'badge badge-danger',
        };
        return <span className={classes[status] || 'badge'}>{labels[status] || status}</span>;
    };

    const getDefinitionTitle = (interviewId) => {
        return definitions[interviewId]?.title || interviewId;
    };

    const getQuestionCount = (interviewId) => {
        return definitions[interviewId]?.questions?.length || '?';
    };

    const getQuestionText = (interviewId, questionId) => {
        const def = definitions[interviewId];
        if (!def) return questionId;
        const q = def.questions.find(q => q.id === questionId);
        return q ? q.text : questionId;
    };

    const exportCSV = () => {
        if (!submissions.length) return;
        const rows = [];
        rows.push(['Usuario', 'Encuesta', 'Estado', 'Pregunta', 'Respuesta', 'Fecha']);

        submissions.forEach(sub => {
            const title = getDefinitionTitle(sub.interview_id);
            Object.entries(sub.answers || {}).forEach(([qId, answer]) => {
                rows.push([
                    sub.user_id,
                    title,
                    sub.status,
                    getQuestionText(sub.interview_id, qId),
                    String(answer),
                    sub.updated_at || '',
                ]);
            });
        });

        const csvContent = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `submissions_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="submissions-page container">
            <div className="page-header">
                <h1>📊 Respuestas</h1>
                <button onClick={exportCSV} className="btn-secondary" disabled={!submissions.length}>
                    ⬇ Exportar CSV
                </button>
            </div>

            {/* Filters */}
            <div className="filters-bar">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="in_progress">En progreso</option>
                        <option value="completed">Completadas</option>
                        <option value="refused">Rechazadas</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>Usuario</label>
                    <input
                        type="text"
                        value={filterUser}
                        onChange={(e) => setFilterUser(e.target.value)}
                        placeholder="email del usuario..."
                    />
                </div>
                <button onClick={loadData} className="btn-secondary btn-sm">Filtrar</button>
            </div>

            {error && <div className="error-msg">{error}</div>}

            {loading ? (
                <p className="loading-text">Cargando...</p>
            ) : submissions.length === 0 ? (
                <div className="empty-state">
                    <p>No hay respuestas{filterStatus ? ` con estado "${filterStatus}"` : ''}.</p>
                </div>
            ) : (
                <div className="submissions-list">
                    {submissions.map((sub) => {
                        const answeredCount = Object.keys(sub.answers || {}).length;
                        const totalCount = getQuestionCount(sub.interview_id);
                        const isExpanded = expandedId === sub._id;

                        return (
                            <div key={sub._id} className={`submission-card ${isExpanded ? 'expanded' : ''}`}>
                                <div className="submission-header" onClick={() => toggleExpand(sub._id)}>
                                    <div className="submission-info">
                                        <span className="submission-user">{sub.user_id}</span>
                                        <span className="submission-interview">{getDefinitionTitle(sub.interview_id)}</span>
                                    </div>
                                    <div className="submission-meta">
                                        <span className="submission-progress">{answeredCount}/{totalCount}</span>
                                        {getStatusBadge(sub.status)}
                                        {sub.status === 'refused' && (
                                            <button
                                                className="btn-text-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReactivate(sub._id);
                                                }}
                                            >
                                                Reactiva
                                            </button>
                                        )}
                                        <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="submission-detail">
                                        <table className="answers-table">
                                            <thead>
                                                <tr>
                                                    <th>Pregunta</th>
                                                    <th>Respuesta</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(sub.answers || {}).map(([qId, answer]) => (
                                                    <tr key={qId}>
                                                        <td className="question-cell">{getQuestionText(sub.interview_id, qId)}</td>
                                                        <td className="answer-cell">{String(answer)}</td>
                                                    </tr>
                                                ))}
                                                {Object.keys(sub.answers || {}).length === 0 && (
                                                    <tr>
                                                        <td colSpan={2} className="center">Sin respuestas aún</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                        <div className="submission-footer">
                                            <span>Creada: {new Date(sub.created_at).toLocaleString('es-MX')}</span>
                                            <span>Actualizada: {new Date(sub.updated_at).toLocaleString('es-MX')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Submissions;
